import React, { useCallback, useRef, useState } from 'react';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { Button as CustomButton } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import { getUltimaPosicaoGeral, updateControleDescargaRegistro, updateControleDescargaRegistroAtivo, criarDescargaManual, atualizarDescargaManual, excluirDescargaManual } from '@spark/services';
import { notification } from '@spark/ui-components/utils/notification';
import { resolveConfiguracoesListagemEnabled } from '@spark/ui-components/utils/resolveConfiguracoesListagemEnabled';
import { useAuth } from '@/context';
import { COGNITO_GROUP_CONFIGURACOES_LISTAGEM_GERAL } from '@/utils/cognitoGroups';
import { ApiRefreshIndicator } from '../../../DisponibilidadeFrotaPatio/components/RefreshIndicator';
import { getPositionModalErrorMessage } from '../../../DisponibilidadeFrotaPatio/utils/positions';
import DescargaAuditoriaModal from './DescargaAuditoriaModal';
import DescargasFilter from './DescargasFilter';
import DescargasHeader from './DescargasHeader';
import DescargaEdicaoRapidaModal from './DescargaEdicaoRapidaModal';
import DescargaManualFormModal from './DescargaManualFormModal';
import DescargaLocalizacaoModal from './DescargaLocalizacaoModal';
import DescargasTable from './DescargasTable';
import { useDescargasListagem } from '../hooks';
import { buildListagemExportConfig, listagemConfig } from '../config/listagemConfig';
import DescargaExportButtons from '../../shared/components/DescargaExportButtons';
import { useControleDescargaExport } from '../../shared/hooks/useControleDescargaExport';
import { usePresentationFullscreen } from '../../shared/hooks/usePresentationFullscreen';
import { useHistory } from 'react-router-dom';

const listagemExportConfig = buildListagemExportConfig(listagemConfig);

const emptyLocalizacaoState = {
  show: false,
  row: null,
  loading: false,
  error: null,
  posicao: null
};

const cleanValue = (value) => {
  const normalized = String(value || '').trim();
  return normalized && normalized !== '-' ? normalized : '';
};

const getRegistroKey = (row) => {
  const pedCodigo = cleanValue(row?.pedCodigo);
  const cliCodigo = cleanValue(row?.cliCodigo || row?.cnpjDestinatario);
  const carCodigo = cleanValue(row?.carCodigo);

  if (!pedCodigo || !cliCodigo || !carCodigo) return null;

  return {
    pedCodigo,
    cliCodigo,
    carCodigo
  };
};

const buildPayloadFromRow = (row) => ({
  observacoes: row?.observacoesDescargaOriginal ?? row?.raw?.observacoesDescarga ?? row?.raw?.ObservacoesDescarga ?? null
});

const isAtivoValue = (value) => {
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() !== 'false';
};

const getAtivoPayloadValue = (row) => {
  const value = row?.ativo ?? row?.raw?.ativo ?? row?.raw?.Ativo ?? false;
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() === 'true';
};

const normalizePosicaoResponse = (response, row) => {
  const posicao = response?.posicao || response?.Posicao || response || {};
  const latitude = Number(posicao?.latitude ?? posicao?.Latitude ?? response?.latitude ?? response?.Latitude);
  const longitude = Number(posicao?.longitude ?? posicao?.Longitude ?? response?.longitude ?? response?.Longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    latitude,
    longitude,
    placa: response?.placa || response?.Placa || row?.placaVeiculo || '',
    frota: response?.frota || response?.Frota || row?.numeroFrota || '',
    dataPosicao: posicao?.dataPosicao ?? posicao?.DataPosicao ?? posicao?.dataAtualizacao ?? posicao?.DataAtualizacao ?? null,
    cidade: posicao?.cidade ?? posicao?.Cidade ?? '',
    estado: posicao?.estado ?? posicao?.Estado ?? '',
    logradouro: posicao?.logradouro ?? posicao?.Logradouro ?? '',
    integracao: response?.integracao ?? response?.Integracao ?? posicao?.integracao ?? posicao?.Integracao ?? null
  };
};

const DescargasCard = ({ enableConfiguracoesListagem }) => {
  const history = useHistory();
  const { hasCognitoGroup } = useAuth();
  const configuracoesEnabled = resolveConfiguracoesListagemEnabled(enableConfiguracoesListagem);
  const podeSalvarGeral = hasCognitoGroup(COGNITO_GROUP_CONFIGURACOES_LISTAGEM_GERAL);

  const [auditoriaRow, setAuditoriaRow] = useState(null);
  const [showAuditoriaGeral, setShowAuditoriaGeral] = useState(false);
  const [edicaoRapidaRow, setEdicaoRapidaRow] = useState(null);
  const [edicaoRapidaLoading, setEdicaoRapidaLoading] = useState(false);
  const [manualModal, setManualModal] = useState({ show: false, mode: 'create', row: null });
  const [manualModalLoading, setManualModalLoading] = useState(false);
  const [localizacaoModal, setLocalizacaoModal] = useState(emptyLocalizacaoState);
  const [loadingAcao, setLoadingAcao] = useState(null);
  const [listagemReady, setListagemReady] = useState(!configuracoesEnabled);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const fullscreenContainerRef = useRef(null);
  const { isFullscreen, toggleFullscreen } = usePresentationFullscreen();

  const {
    data,
    loading,
    filterLoading,
    totalRegistros,
    filtros,
    paginationState,
    handleFilterApply,
    applyConfiguracaoFiltros,
    handlePaginationChange,
    fetchData
  } = useDescargasListagem({
    readyToFetch: listagemReady
  });

  const {
    loadingExport,
    exportDisabled,
    handleExportExcel,
    handleExportPdf,
    handleColumnLayoutChange
  } = useControleDescargaExport({
    filtros,
    exportConfig: listagemExportConfig,
    disabled: loading || filterLoading || totalRegistros === 0,
    excelFileName: 'descargas-controle.xlsx',
    pdfFileName: 'descargas-controle.pdf'
  });

  const handleConfiguracaoReady = useCallback(() => {
    setListagemReady(true);
  }, []);

  const handleRefreshDescargas = useCallback(async () => {
    await fetchData();
    setLastUpdatedAt(new Date().toISOString());
  }, [fetchData]);

  const handleRowClick = (row) => {
    history.push('/pages/descarga/descargas/detalhes', {
      descarga: row,
      returnPath: '/pages/descarga/descargas'
    });
  };

  const handleOpenEdicaoRapida = (row) => {
    if (row?.isDescargaManual) {
      setManualModal({ show: true, mode: 'edit', row });
      return;
    }

    setEdicaoRapidaRow(row);
  };

  const handleOpenCriarManual = () => {
    setManualModal({ show: true, mode: 'create', row: null });
  };

  const handleCloseManualModal = () => {
    if (manualModalLoading) return;
    setManualModal({ show: false, mode: 'create', row: null });
  };

  const handleSalvarManual = async (payload) => {
    try {
      setManualModalLoading(true);

      if (manualModal.mode === 'edit') {
        const manualId = manualModal.row?.descargaManualId;
        if (!manualId) {
          notification.error('Nao foi possivel identificar o registro manual para salvar.');
          return;
        }

        await atualizarDescargaManual(manualId, payload);
        notification.success('Descarga manual atualizada com sucesso!');
      } else {
        await criarDescargaManual(payload);
        notification.success('Descarga manual criada com sucesso!');
      }

      setManualModal({ show: false, mode: 'create', row: null });
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar descarga manual:', error);
      notification.error('Nao foi possivel salvar a descarga manual.');
    } finally {
      setManualModalLoading(false);
    }
  };

  const handleExcluirManual = async (row) => {
    const manualId = row?.descargaManualId;
    if (!manualId) {
      notification.error('Nao foi possivel identificar o registro manual para excluir.');
      return;
    }

    const confirmar = window.confirm(`Deseja excluir a descarga manual ${row?.numeroCarga || ''}?`);
    if (!confirmar) return;

    try {
      setLoadingAcao({ id: row.id, acao: 'excluir-manual' });
      await excluirDescargaManual(manualId);
      notification.success('Descarga manual excluida com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir descarga manual:', error);
      notification.error('Nao foi possivel excluir a descarga manual.');
    } finally {
      setLoadingAcao(null);
    }
  };

  const handleCloseEdicaoRapida = () => {
    if (edicaoRapidaLoading) return;
    setEdicaoRapidaRow(null);
  };

  const handleSalvarEdicaoRapida = async (payload) => {
    const registroKey = getRegistroKey(edicaoRapidaRow);
    if (!registroKey) {
      notification.error('Nao foi possivel identificar os codigos da descarga para salvar.');
      return;
    }

    try {
      setEdicaoRapidaLoading(true);
      await updateControleDescargaRegistro(registroKey, {
        ...payload,
        ativo: getAtivoPayloadValue(edicaoRapidaRow)
      });
      notification.success('Descarga atualizada com sucesso!');
      setEdicaoRapidaRow(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar descarga:', error);
      notification.error('Nao foi possivel atualizar a descarga.');
    } finally {
      setEdicaoRapidaLoading(false);
    }
  };

  const handleAuditoria = (row) => {
    setLoadingAcao({ id: row.id, acao: 'auditoria' });
    setAuditoriaRow(row);
    setLoadingAcao(null);
  };

  const handleCloseAuditoria = () => {
    setAuditoriaRow(null);
  };

  const handleOpenAuditoriaGeral = () => {
    setShowAuditoriaGeral(true);
  };

  const handleCloseAuditoriaGeral = () => {
    setShowAuditoriaGeral(false);
  };

  const handleLocalizacao = async (row) => {
    const placa = cleanValue(row?.placaVeiculo);
    const frota = cleanValue(row?.numeroFrota);

    setLocalizacaoModal({
      show: true,
      row,
      loading: true,
      error: null,
      posicao: null
    });

    if (!placa && !frota) {
      setLocalizacaoModal({
        show: true,
        row,
        loading: false,
        error: 'Nao foi possivel identificar a placa ou frota do cavalo.',
        posicao: null
      });
      return;
    }

    try {
      setLoadingAcao({ id: row.id, acao: 'localizacao' });
      const response = await getUltimaPosicaoGeral({ placa, frota });
      const posicao = normalizePosicaoResponse(response, row);

      setLocalizacaoModal({
        show: true,
        row,
        loading: false,
        error: posicao ? null : 'Localizacao nao encontrada para a placa/frota informada.',
        posicao
      });
    } catch (error) {
      console.error('Erro ao consultar localizacao da descarga:', error);
      setLocalizacaoModal({
        show: true,
        row,
        loading: false,
        error: getPositionModalErrorMessage(error),
        posicao: null
      });
    } finally {
      setLoadingAcao(null);
    }
  };

  const handleCloseLocalizacao = () => {
    setLocalizacaoModal(emptyLocalizacaoState);
  };

  const handleAlterarStatus = async (row) => {
    const registroKey = getRegistroKey(row);
    if (!registroKey) {
      notification.error('Nao foi possivel identificar os codigos da descarga para alterar o status.');
      return;
    }

    const nextAtivo = !isAtivoValue(row?.ativo);
    const acao = nextAtivo ? 'ativar' : 'inativar';
    const confirmar = window.confirm(`Deseja ${acao} a descarga ${row?.numeroCarga || ''}?`);
    if (!confirmar) return;

    try {
      setLoadingAcao({ id: row.id, acao: 'status' });
      await updateControleDescargaRegistroAtivo(registroKey, nextAtivo, buildPayloadFromRow(row));
      notification.success(`Descarga ${nextAtivo ? 'ativada' : 'inativada'} com sucesso!`);
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status da descarga:', error);
      notification.error('Nao foi possivel alterar o status da descarga.');
    } finally {
      setLoadingAcao(null);
    }
  };

  return (
    <>
      <Card>
        <CardBody>
          <DescargasHeader
            totalRegistros={totalRegistros}
            onMostrarAuditoriaGeral={handleOpenAuditoriaGeral}
            actions={(
              <>
                <CustomButton
                  variant="outline-primary"
                  size="sm"
                  onClick={handleOpenCriarManual}
                >
                  Nova descarga manual
                </CustomButton>
                <DescargaExportButtons
                  onExportExcel={handleExportExcel}
                  onExportPdf={handleExportPdf}
                  loadingExport={loadingExport}
                  disabled={exportDisabled}
                />
                <ApiRefreshIndicator
                  enabled
                  onRefresh={handleRefreshDescargas}
                  loading={loading || filterLoading}
                  lastUpdatedAt={lastUpdatedAt}
                  initialPaused
                />
                <CustomButton
                  variant="outline-info"
                  size="sm"
                  onClick={toggleFullscreen}
                  title="Alternar tela cheia da tabela"
                  aria-label="Alternar tela cheia da tabela"
                >
                  <Icon name={isFullscreen ? 'fullscreen-exit' : 'fullscreen'} size={18} />
                </CustomButton>
              </>
            )}
          />
      
          <DescargasFilter
            filtros={filtros}
            loading={filterLoading}
            onApply={handleFilterApply}
            onApplyConfiguracao={applyConfiguracaoFiltros}
            onConfiguracaoReady={handleConfiguracaoReady}
            podeSalvarGeral={podeSalvarGeral}
            configuracoesListagem={configuracoesEnabled}
          >
            
            <DescargasTable
              data={data}
              loading={loading}
              totalRegistros={totalRegistros}
              paginationState={paginationState}
              onPaginationChange={handlePaginationChange}
              onDetalhes={handleRowClick}
              onEditarRapida={handleOpenEdicaoRapida}
              onAuditoria={handleAuditoria}
              onLocalizacao={handleLocalizacao}
              onAlterarStatus={handleAlterarStatus}
              onExcluirManual={handleExcluirManual}
              loadingAcao={loadingAcao}
              onColumnLayoutChange={handleColumnLayoutChange}
              fullscreenContainerRef={fullscreenContainerRef}
              isFullscreen={isFullscreen}
            />
          </DescargasFilter>
        </CardBody>
      </Card>

      <DescargaAuditoriaModal
        show={!!auditoriaRow}
        row={auditoriaRow}
        onClose={handleCloseAuditoria}
      />

      <DescargaAuditoriaModal
        show={showAuditoriaGeral}
        geral
        onClose={handleCloseAuditoriaGeral}
      />

      <DescargaEdicaoRapidaModal
        show={!!edicaoRapidaRow}
        row={edicaoRapidaRow}
        loading={edicaoRapidaLoading}
        onClose={handleCloseEdicaoRapida}
        onSubmit={handleSalvarEdicaoRapida}
      />

      <DescargaManualFormModal
        show={manualModal.show}
        mode={manualModal.mode}
        row={manualModal.row}
        loading={manualModalLoading}
        onClose={handleCloseManualModal}
        onSubmit={handleSalvarManual}
      />

      <DescargaLocalizacaoModal
        show={localizacaoModal.show}
        row={localizacaoModal.row}
        loading={localizacaoModal.loading}
        error={localizacaoModal.error}
        posicao={localizacaoModal.posicao}
        onClose={handleCloseLocalizacao}
      />
    </>
  );
};

export default DescargasCard;
